<script setup>
import { ref, onBeforeMount, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router"; // Importa useRoute para acceder a los parámetros de la ruta

// import Navbar from "@/examples/PageLayout/Navbar.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import PasswordInput from "@/components/PasswordInput.vue";
import ErrorAlert from "@/components/ErrorAlert.vue";

const body = document.getElementsByTagName("body")[0];
const store = useStore();
const router = useRouter();
const route = useRoute();

const email = ref(route.query.email || '');
const password = ref('');
const rememberMe = ref(false);
const error = ref(null);
const rememberMessage = ref(null);

// Limpiar error cuando el usuario empiece a escribir
const clearError = () => {
  if (error.value) {
    error.value = null;
  }
  if (rememberMessage.value) {
    rememberMessage.value = null;
  }
};

// Funciones para manejar "Acuérdate de mí"
const loadSavedCredentials = () => {
  try {
    // Si hay email en la URL, tiene prioridad
    if (route.query.email) {
      email.value = route.query.email;
      return;
    }
    
    // Cargar credenciales guardadas
    const savedEmail = localStorage.getItem('idamedic_remember_email');
    const savedRemember = localStorage.getItem('idamedic_remember_me');
    
    if (savedEmail && savedRemember === 'true') {
      email.value = savedEmail;
      rememberMe.value = true;
      rememberMessage.value = "Se han cargado tus credenciales guardadas";
      
      // Limpiar el mensaje después de 3 segundos
      setTimeout(() => {
        rememberMessage.value = null;
      }, 3000);
    }
  } catch (error) {
    // Error silencioso en producción
  }
};

const saveCredentials = () => {
  try {
    if (rememberMe.value && email.value) {
      // Solo actualizar si "Acuérdate de mí" está marcado y hay email
      localStorage.setItem('idamedic_remember_email', email.value);
      localStorage.setItem('idamedic_remember_me', 'true');
    }
  } catch (error) {
    // Error silencioso en producción
  }
};

const clearSavedCredentials = () => {
  try {
    localStorage.removeItem('idamedic_remember_email');
    localStorage.removeItem('idamedic_remember_me');
  } catch (error) {
    // Error silencioso en producción
  }
};

// Watcher para manejar cambios en "Acuérdate de mí"
watch(rememberMe, (newValue) => {
  if (newValue) {
    handleRememberMeChange();
  } else {
    clearSavedCredentials();
    // También limpiar el email del input cuando se desactive
    email.value = '';
  }
});

// Watcher para el email - actualizar localStorage si "Acuérdate de mí" está marcado
watch(email, () => {
  if (rememberMe.value) {
    handleRememberMeChange();
  }
});

// Función para manejar el guardado cuando "Acuérdate de mí" está activo
const handleRememberMeChange = () => {
  if (email.value && email.value.trim() !== '') {
    try {
      localStorage.setItem('idamedic_remember_email', email.value);
      localStorage.setItem('idamedic_remember_me', 'true');
    } catch (error) {
      // Error silencioso en producción
    }
  }
};

const toggleDefaultLayout = () => store.commit("toggleDefaultLayout");

onBeforeMount(() => {
  store.state.hideConfigButton = true;
  toggleDefaultLayout();
  body.classList.remove("bg-gray-100");
  
  // Cargar credenciales guardadas si existen
  loadSavedCredentials();
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  toggleDefaultLayout();
  body.classList.add("bg-gray-100");
});

const handleApiError = (err) => {
  if (err.response) {
    const { status, data } = err.response;
    
    switch (status) {
      case 400:
        error.value = data.message || 'Solicitud incorrecta.';
        break;
        
      case 401:
        error.value = data.message || 'Credenciales inválidas. Verifica tu correo electrónico y contraseña.';
        // No redirigir en la página de login, mantener el mensaje visible
        break;
        
      case 403:
        error.value = data.message || 'No tienes permisos para esta acción.';
        break;
        
      case 404:
        error.value = data.message || 'Recurso no encontrado.';
        break;
        
      case 422:
        // Errores de validación
        if (data.errors) {
          const firstError = Object.values(data.errors)[0];
          error.value = Array.isArray(firstError) ? firstError[0] : firstError;
        } else {
          error.value = data.message || 'Error de validación.';
        }
        break;
        
      case 429:
        error.value = data.message || 'Demasiadas solicitudes. Espera un momento.';
        break;
        
      case 500:
        error.value = data.message || 'Error del servidor. Intenta más tarde.';
        break;
        
      case 503:
        error.value = data.message || 'Servicio no disponible. Intenta más tarde.';
        break;
        
      default:
        error.value = data.message || `Error ${status}. Por favor, intenta nuevamente.`;
    }
  } else if (err.request) {
    // Sin respuesta del servidor
    error.value = 'Sin conexión al servidor. Verifica tu conexión a internet.';
  } else {
    // Error de configuración
    error.value = 'Error inesperado. Por favor, intenta nuevamente.';
  }
};

const handleLogin = async () => {
  error.value = null; // Limpiar error anterior solo cuando se intenta un nuevo login
  try {
    // Despacha la acción de login del store
    const response = await store.dispatch('auth/login', {
      email: email.value,
      password: password.value,
    });
    
    if (response) {
      // Manejar "Acuérdate de mí" después de un login exitoso
      saveCredentials();
      
      // Esperar un tick para asegurar que el estado se actualizó completamente
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Navegar a la página de pacientes
      await router.push('/pacientes');
    }
  } catch (err) {
    // No limpiar credenciales guardadas en caso de error de login
    // El usuario puede querer intentar nuevamente con las mismas credenciales
    
    handleApiError(err);
    // El error se mantiene visible hasta el próximo intento de login
  }
};
</script>

<template>
  <div class="container top-0 position-sticky z-index-sticky">
    <div class="row">
      <div class="col-12">
        <!-- <navbar is-blur="blur  border-radius-lg my-3 py-2 start-0 end-0 mx-4 shadow"
          btn-background="bg-gradient-success" :dark-mode="true" /> -->
      </div>
    </div>
  </div>
  <main class="main-content mt-0">
    <section>
      <div class="page-header min-vh-100">
        <div class="container">
          <div class="row">
            <div class="col-xl-4 col-lg-5 col-md-7 d-flex flex-column mx-lg-0 mx-auto">
              <div class="card card-plain">
                <div class="card-header pb-0 text-left">
                  <h4 class="font-weight-bolder">Iniciar sesión</h4>
                  <p class="mb-0">Ingresa tu email y contraseña para iniciar sesión</p>
                </div>
                <div class="card-body pb-3">
                  <form @submit.prevent="handleLogin">
                    <label>Email</label>
                    <argon-input 
                      id="email" 
                      v-model="email" 
                      type="email" 
                      placeholder="Email" 
                      aria-label="Email"
                      @input="clearError"
                      @blur="handleRememberMeChange"
                    />
                    <label>Password</label>
                    <PasswordInput 
                      id="password" 
                      v-model="password" 
                      placeholder="Password"
                      aria-label="Password"
                      @input="clearError"
                    />
                    <div class="mt-3">
                      <argon-switch 
                        id="rememberMe" 
                        v-model="rememberMe"
                        name="rememberMe"
                        @change="handleRememberMeChange"
                      >
                        Acuérdate de mí
                      </argon-switch>
                    </div>
                    <div class="text-center">
                      <argon-button type="submit" color="success" variant="gradient" full-width class="mt-4 mb-0">Iniciar
                        sesión</argon-button>
                    </div>
                    <div class="text-center mt-3">
                      <router-link :to="{ name: 'ForgotPassword' }" class="text-muted text-sm">
                        ¿Olvidaste tu contraseña?
                      </router-link>
                    </div>
                  </form>
                  
                  <!-- Mensaje de credenciales recordadas -->
                  <ErrorAlert v-if="rememberMessage" :error="rememberMessage" type="info" />
                  
                  <!-- Mensaje de error -->
                  <ErrorAlert :error="error" type="danger" />
                </div>
                <div class="px-1 pt-0 text-center card-footer px-lg-2">
                  <p class="mx-auto mb-4 text-sm">
                    ¿No tienes una cuenta?
                    <router-link
:to="{ name: 'Register' }"
                      class="text-success text-gradient font-weight-bold">Registrarse</router-link>
                  </p>
                </div>
              </div>
            </div>
            <div
              class="col-6 d-lg-flex d-none h-100 my-auto pe-0 position-absolute top-0 end-0 text-center justify-content-center flex-column">
              <div class="login-hero m-3 border-radius-lg">
                <div class="hero-mesh mesh-1"></div>
                <div class="hero-mesh mesh-2"></div>
                <div class="hero-glow glow-1"></div>
                <div class="hero-glow glow-2"></div>
                
                <!-- Blindaje de información -->
                <div class="data-shield">
                  <div class="shield-layer layer-1"></div>
                  <div class="shield-layer layer-2"></div>
                  <div class="shield-layer layer-3"></div>
                  <div class="shield-core">
                    <i class="fas fa-lock"></i>
                  </div>
                  <div class="data-particles">
                    <span class="particle"></span>
                    <span class="particle"></span>
                    <span class="particle"></span>
                    <span class="particle"></span>
                    <span class="particle"></span>
                    <span class="particle"></span>
                  </div>
                </div>

                <div class="hero-content">
                  <div class="hero-logo">
                    <img src="@/assets/img/logo-ct-dark.png" alt="iDAMedic Keops" />
                    <span>iDAMedic Keops</span>
                  </div>

                  <h2 class="hero-title">Expediente Digital Inteligente</h2>
                  <p class="hero-description">
                    Cumplimiento normativo estricto con <strong>NOM-004-SSA3-2012</strong> y <strong>NOM-024-SSA3-2012</strong>.
                    Auditoría completa versionada por usuario y campo, garantizando trazabilidad total.
                  </p>

                  <div class="hero-card-grid">
                    <div class="hero-card">
                      <i class="fas fa-file-signature"></i>
                      <div>
                        <p class="hero-card-title">Auditoría estricta</p>
                        <p class="hero-card-text">Control versionado de cada cambio por usuario y campo.</p>
                      </div>
                    </div>
                    <div class="hero-card">
                      <i class="fas fa-history"></i>
                      <div>
                        <p class="hero-card-title">Trazabilidad total</p>
                        <p class="hero-card-text">Cada acción registrada con roles y permisos normados según SSA.</p>
                      </div>
                    </div>
                    <div class="hero-card">
                      <i class="fas fa-shield-check"></i>
                      <div>
                        <p class="hero-card-title">Seguridad certificada</p>
                        <p class="hero-card-text">Trazabilidad completa: anterior, nuevo, usuario, fecha/hora y motivo.</p>
                      </div>
                    </div>
                  </div>

                  <div class="hero-status">
                    <div class="status-indicator">
                      <span class="status-dot"></span>
                      Infraestructura operativa
                    </div>
                    <span class="status-value">Tiempo real</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.login-container {
  max-width: 400px;
  margin: 0 auto;
  padding: 1rem;
}

/* Animaciones de entrada */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in-up {
  animation: fadeInUp 0.8s ease-out forwards;
  opacity: 0;
}

.fade-in-up.delay-1 {
  animation-delay: 0.2s;
}

.fade-in-up.delay-2 {
  animation-delay: 0.4s;
}

.fade-in-up.delay-3 {
  animation-delay: 0.6s;
}

/* Logo animado */
.animated-logo {
  animation: float 3s ease-in-out infinite;
}

.logo-image {
  width: 120px;
  height: auto;
  filter: brightness(0) invert(1);
  animation: logoGlow 3s ease-in-out infinite;
}

@keyframes logoGlow {
  0%, 100% {
    filter: brightness(0) invert(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
  }
  50% {
    filter: brightness(0) invert(1) drop-shadow(0 0 20px rgba(255, 255, 255, 0.6));
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-15px);
  }
}

.login-hero {
  position: relative;
  height: calc(100% - 1.5rem);
  background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1), transparent 60%),
              linear-gradient(135deg, #020c26 0%, #062a5a 50%, #090c1a 100%);
  padding: 3.5rem;
  display: flex;
  align-items: center;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(2, 12, 38, 0.55);
}

.hero-mesh {
  position: absolute;
  width: 220%;
  height: 220%;
  background: radial-gradient(circle, rgba(28, 233, 182, 0.15) 0%, transparent 45%);
  animation: meshMove 18s ease-in-out infinite;
  opacity: 0.8;
}

.mesh-1 {
  left: -80%;
  top: -80%;
}

.mesh-2 {
  right: -70%;
  bottom: -70%;
  animation-direction: reverse;
}

.hero-glow {
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.55;
}

.glow-1 {
  background: #38bdf8;
  top: -50px;
  right: 40px;
}

.glow-2 {
  background: #4ade80;
  bottom: -80px;
  left: 20px;
}

/* Blindaje de información - Animación sofisticada */
.data-shield {
  position: absolute;
  top: 50%;
  right: 15%;
  transform: translate(0, -50%);
  width: 280px;
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.shield-layer {
  position: absolute;
  border: 1px solid;
  border-radius: 50%;
  opacity: 0.4;
}

.layer-1 {
  width: 100%;
  height: 100%;
  border-color: rgba(59, 130, 246, 0.3);
  animation: shieldRotate 20s linear infinite;
}

.layer-2 {
  width: 75%;
  height: 75%;
  border-color: rgba(96, 165, 250, 0.4);
  animation: shieldRotate 15s linear infinite reverse;
  border-style: dashed;
}

.layer-3 {
  width: 50%;
  height: 50%;
  border-color: rgba(147, 197, 253, 0.5);
  animation: shieldRotate 10s linear infinite;
  border-width: 2px;
}

.shield-core {
  position: relative;
  z-index: 2;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(96, 165, 250, 0.1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(96, 165, 250, 0.6);
  box-shadow: 
    0 0 30px rgba(59, 130, 246, 0.3),
    inset 0 0 20px rgba(96, 165, 250, 0.2);
  animation: corePulse 4s ease-in-out infinite;
}

.shield-core i {
  font-size: 2rem;
  color: #60a5fa;
  filter: drop-shadow(0 0 10px rgba(96, 165, 250, 0.8));
  animation: lockBreathe 3s ease-in-out infinite;
}

.data-particles {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(96, 165, 250, 0.8);
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(96, 165, 250, 0.6);
  animation: particleFloat 8s ease-in-out infinite;
}

.particle:nth-child(1) {
  top: 10%;
  left: 20%;
  animation-delay: 0s;
}

.particle:nth-child(2) {
  top: 30%;
  right: 15%;
  animation-delay: 1.3s;
}

.particle:nth-child(3) {
  bottom: 25%;
  left: 10%;
  animation-delay: 2.6s;
}

.particle:nth-child(4) {
  top: 60%;
  right: 25%;
  animation-delay: 4s;
}

.particle:nth-child(5) {
  bottom: 15%;
  right: 10%;
  animation-delay: 5.3s;
}

.particle:nth-child(6) {
  top: 45%;
  left: 5%;
  animation-delay: 6.6s;
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: left;
  color: white;
  max-width: 520px;
}

.hero-logo {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.45rem 1.25rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(6px);
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.hero-logo img {
  width: 28px;
  height: 28px;
  filter: brightness(0) invert(1);
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #ffffff;
}

.hero-description {
  color: rgba(255, 255, 255, 0.78);
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
}

.hero-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.hero-card {
  display: flex;
  gap: 0.75rem;
  padding: 1.1rem 1rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(6px);
}

.hero-card:nth-child(3) {
  grid-column: span 2;
}

.hero-card i {
  font-size: 1.2rem;
  margin-top: 0.2rem;
}

.hero-card-title {
  margin: 0;
  font-weight: 600;
}

.hero-card-text {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
}

.hero-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1.2rem;
  border-radius: 1rem;
  background: rgba(2, 8, 23, 0.6);
  border: 1px solid rgba(56, 189, 248, 0.3);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 12px rgba(52, 211, 153, 0.6);
  animation: pulseDot 1.8s ease-in-out infinite;
}

.status-value {
  font-weight: 600;
}

@keyframes meshMove {
  0% {
    transform: translate(-10%, -10%) scale(1);
  }
  50% {
    transform: translate(10%, 10%) scale(1.05);
  }
  100% {
    transform: translate(-10%, -10%) scale(1);
  }
}

@keyframes shieldRotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes corePulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 
      0 0 30px rgba(59, 130, 246, 0.3),
      inset 0 0 20px rgba(96, 165, 250, 0.2);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 
      0 0 40px rgba(59, 130, 246, 0.5),
      inset 0 0 30px rgba(96, 165, 250, 0.3);
  }
}

@keyframes lockBreathe {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.9;
  }
}

@keyframes particleFloat {
  0%, 100% {
    transform: translate(0, 0);
    opacity: 0.3;
  }
  25% {
    transform: translate(10px, -15px);
    opacity: 0.7;
  }
  50% {
    transform: translate(-5px, -25px);
    opacity: 1;
  }
  75% {
    transform: translate(-15px, -10px);
    opacity: 0.7;
  }
}

@keyframes pulseDot {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.4);
    opacity: 0.6;
  }
}
</style>
